import { useState } from 'react';
import { Trash2, MoreVertical, Copy, RefreshCw } from 'lucide-react';
import { ExpandableRow, type RowTone } from './ui/ListRows';
import type { ServiceProviderType, ServiceStatus, BillingInterval } from '../types/serviceProvider';
import type { ServiceProvider } from '../types/serviceProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { BILLING_INTERVAL_CONFIG } from '../types/serviceProvider';

export interface ServiceBlockData {
  id: string;
  service_provider_id: string | null;
  service_provider_name: string;
  service_type: ServiceProviderType;
  status: ServiceStatus;
  agreed_fee: number;
  description: string;
  notes: string;
  existing?: boolean;
  display_order?: number;
  is_recurring: boolean;
  billing_interval: BillingInterval | null;
  custom_interval_days: number | null;
  recurring_start_date: string;
  recurring_end_date: string;
}

interface CampaignServiceBlockProps {
  block: ServiceBlockData;
  availableProviders: ServiceProvider[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (block: ServiceBlockData) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  canRemove: boolean;
  removeDisabledReason?: string;
  dragHandleProps?: {
    attributes: Record<string, any>;
    listeners: Record<string, any>;
  };
}

export function CampaignServiceBlock({
  block,
  availableProviders,
  isExpanded,
  onToggleExpand,
  onChange,
  onRemove,
  onDuplicate,
  canRemove,
  removeDisabledReason,
  dragHandleProps
}: CampaignServiceBlockProps) {
  const { t } = useLanguage();
  const [showActions, setShowActions] = useState(false);

  const handleProviderChange = (name: string) => {
    const provider = availableProviders.find(p => p.name === name);
    onChange({
      ...block,
      service_provider_name: name,
      service_provider_id: provider?.id || null,
      service_type: provider?.type || block.service_type
    });
  };

  const handleFieldChange = (field: keyof ServiceBlockData, value: any) => {
    onChange({
      ...block,
      [field]: value
    });
  };

  const handleRecurringToggle = () => {
    onChange({
      ...block,
      is_recurring: !block.is_recurring,
      billing_interval: !block.is_recurring ? 'monthly' : null,
      custom_interval_days: null,
      recurring_start_date: !block.is_recurring ? new Date().toISOString().split('T')[0] : '',
      recurring_end_date: '',
    });
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(amount);

  const getRecurringSummary = (): string | null => {
    if (!block.is_recurring || !block.billing_interval) return null;
    const intervalLabel = BILLING_INTERVAL_CONFIG[block.billing_interval]?.labelPt || block.billing_interval;
    const fee = formatCurrency(block.agreed_fee);
    const start = block.recurring_start_date || '...';
    const end = block.recurring_end_date;
    return `${fee} / ${intervalLabel.toLowerCase()} desde ${start}${end ? ` até ${end}` : ' (sem fim)'}`;
  };

  // Green when completed, blue in progress/confirmed, red cancelled
  const tone: RowTone = block.status === 'Completed' ? 'success'
    : block.status === 'In Progress' || block.status === 'Confirmed' ? 'info'
    : block.status === 'Cancelled' ? 'danger'
    : 'neutral';

  return (
    <ExpandableRow
      tone={tone}
      isExpanded={isExpanded}
      onToggle={onToggleExpand}
      ariaLabel={`${block.service_provider_name || 'Novo serviço'} - ${block.service_type}`}
      dragHandle={dragHandleProps ? { ...dragHandleProps.attributes, ...dragHandleProps.listeners } as React.HTMLAttributes<HTMLButtonElement> : null}
      summary={<>
        <span className="text-sm font-semibold text-gray-900 truncate max-w-[240px]">
          {block.service_provider_name || 'Novo fornecedor'}
        </span>
        <span className="cm-chip cm-chip-neutral">{block.service_type}</span>
        {block.is_recurring && (
          <span className="cm-chip cm-chip-info inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" />
            Recorrente
          </span>
        )}
        {block.description && (
          <span className="hidden md:inline text-xs text-gray-500 truncate min-w-0">{block.description}</span>
        )}
      </>}
      aside={block.agreed_fee > 0 ? (
        <>
          {formatCurrency(block.agreed_fee)}
          {block.is_recurring && block.billing_interval && (
            <span className="text-xs font-normal text-teal-600 ml-1">
              /{BILLING_INTERVAL_CONFIG[block.billing_interval]?.labelPt.toLowerCase().slice(0, 3)}
            </span>
          )}
        </>
      ) : ''}
      actions={
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowActions(!showActions)}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label={t('common.actions')}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {showActions && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowActions(false)}
                />
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-20 min-w-[150px]">
                  <button
                    type="button"
                    onClick={() => {
                      onDuplicate();
                      setShowActions(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" />
                    {t('common.duplicate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (canRemove) {
                        onRemove();
                        setShowActions(false);
                      }
                    }}
                    disabled={!canRemove}
                    title={removeDisabledReason}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('common.remove')}
                  </button>
                </div>
              </>
            )}
          </div>
      }
    >
          <div className="cm-grid grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            {/* Service Provider */}
            <div className="cm-field">
              <label className="cm-label">
                Fornecedor <span className="cm-req">*</span>
              </label>
              {block.existing ? (
                <div className="cm-readonly">{block.service_provider_name}</div>
              ) : (
                <input
                  type="text"
                  list={`providers-${block.id}`}
                  value={block.service_provider_name}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  required
                  placeholder="Escrever ou escolher fornecedor"
                  className="cm-input"
                />
              )}
              <datalist id={`providers-${block.id}`}>
                {availableProviders.map((provider) => (
                  <option key={provider.id} value={provider.name} />
                ))}
              </datalist>
            </div>

            {/* Service Type */}
            <div className="cm-field">
              <label className="cm-label">
                Tipo de serviço <span className="cm-req">*</span>
              </label>
              <select
                value={block.service_type}
                onChange={(e) => handleFieldChange('service_type', e.target.value as ServiceProviderType)}
                required
                className="cm-select"
              >
                <option value="Videographer">Videógrafo</option>
                <option value="Photographer">Fotógrafo</option>
                <option value="Stylist">Stylist</option>
                <option value="Product">Produto</option>
                <option value="Location">Local</option>
                <option value="Equipment">Equipamento</option>
                <option value="Other">Outro</option>
              </select>
            </div>

            {/* Agreed Fee */}
            <div className="cm-field">
              <label className="cm-label">
                {block.is_recurring ? 'Valor por período' : 'Valor acordado'} <span className="cm-req">*</span>
              </label>
              <div className="cm-affix has-left">
                <span>€</span>
                <input
                  type="number"
                  value={block.agreed_fee || ''}
                  onChange={(e) => handleFieldChange('agreed_fee', parseFloat(e.target.value) || 0)}
                  step="0.01"
                  min="0"
                  required
                  className="cm-input cm-numeric"
                />
              </div>
            </div>

            {/* Status */}
            <div className="cm-field">
              <label className="cm-label">
                Estado <span className="cm-req">*</span>
              </label>
              <select
                value={block.status}
                onChange={(e) => handleFieldChange('status', e.target.value as ServiceStatus)}
                required
                className="cm-select"
              >
                <option value="Requested">Pedido</option>
                <option value="Confirmed">Confirmado</option>
                <option value="In Progress">Em curso</option>
                <option value="Completed">Concluído</option>
                <option value="Cancelled">Cancelado</option>
              </select>
            </div>

            {/* Recurring toggle */}
            <div className="cm-field md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={block.is_recurring}
                  onChange={handleRecurringToggle}
                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5 text-teal-600" />
                  Serviço recorrente
                </span>
              </label>
            </div>

            {block.is_recurring && (
              <>
                {/* Billing Interval */}
                <div className="cm-field">
                  <label className="cm-label">
                    Periodicidade <span className="cm-req">*</span>
                  </label>
                  <select
                    value={block.billing_interval || 'monthly'}
                    onChange={(e) => {
                      const val = e.target.value as BillingInterval;
                      onChange({
                        ...block,
                        billing_interval: val,
                        custom_interval_days: val !== 'custom' ? null : block.custom_interval_days,
                      });
                    }}
                    className="cm-select"
                  >
                    {Object.entries(BILLING_INTERVAL_CONFIG).map(([key, cfg]) => (
                      <option key={key} value={key}>{cfg.labelPt}</option>
                    ))}
                  </select>
                </div>

                {block.billing_interval === 'custom' && (
                  <div className="cm-field">
                    <label className="cm-label">
                      Intervalo (dias) <span className="cm-req">*</span>
                    </label>
                    <input
                      type="number"
                      value={block.custom_interval_days || ''}
                      onChange={(e) => handleFieldChange('custom_interval_days', parseInt(e.target.value) || null)}
                      min="1"
                      placeholder="e.g. 45"
                      className="cm-input"
                    />
                  </div>
                )}

                {/* Start Date */}
                <div className="cm-field">
                  <label className="cm-label">
                    Data de início <span className="cm-req">*</span>
                  </label>
                  <input
                    type="date"
                    value={block.recurring_start_date || ''}
                    onChange={(e) => handleFieldChange('recurring_start_date', e.target.value)}
                    required
                    className="cm-date"
                  />
                </div>

                {/* End Date */}
                <div className="cm-field">
                  <label className="cm-label">Data de fim</label>
                  <input
                    type="date"
                    value={block.recurring_end_date || ''}
                    onChange={(e) => handleFieldChange('recurring_end_date', e.target.value)}
                    min={block.recurring_start_date || undefined}
                    className="cm-date"
                  />
                  <p className="cm-caption">Leave empty for ongoing</p>
                </div>

                {getRecurringSummary() && (
                  <div className="md:col-span-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2">
                    <p className="text-sm text-teal-800 flex items-center gap-1.5">
                      <RefreshCw className="w-3.5 h-3.5" />
                      {getRecurringSummary()}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Description */}
            <div className="cm-field md:col-span-2">
              <label className="cm-label">Descrição</label>
              <textarea
                value={block.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                rows={2}
                placeholder="Breve descrição do serviço"
                className="cm-input"
              />
            </div>

            {/* Notes */}
            <div className="cm-field md:col-span-2">
              <label className="cm-label">Notas</label>
              <textarea
                value={block.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                rows={2}
                placeholder="Notas adicionais"
                className="cm-input"
              />
            </div>
          </div>
    </ExpandableRow>
  );
}
